import React, { useEffect, useState, useRef, createElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LockIcon, MailIcon, AlertCircleIcon, Eye, EyeOff, XIcon } from 'lucide-react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
// ─── Cloudflare Turnstile config ────────────────────────────────────────
// Feature flag: flip to `false` to disable the CAPTCHA gate entirely
// (e.g. emergency rollback) without removing any code.
const ENABLE_LOGIN_CAPTCHA = true;
// Number of failed login attempts before Turnstile is required for further
// attempts in the same modal session. 3 catches automation early while
// tolerating honest typos. Matches StudentLogin + TeacherLogin page route.
const LOGIN_CAPTCHA_THRESHOLD = 3;
// Cloudflare Turnstile PUBLIC site key. Safe to commit.
const TURNSTILE_SITE_KEY = '0x4AAAAAADYnh4MLjOOURJjN';
// Cloudflare's Turnstile JS API URL. Loaded once per page via <script>.
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}
interface TeacherLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const TeacherLoginModal = ({
  isOpen,
  onClose
}: TeacherLoginModalProps) => {
  const [email, setEmail] = useState('');
  const [passport, setPassport] = useState('');
  const [showPassport, setShowPassport] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // ─── Failed-attempt tracking + Turnstile state ───────────────────────
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const captchaRequired = ENABLE_LOGIN_CAPTCHA && failedAttempts >= LOGIN_CAPTCHA_THRESHOLD;
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  // ─── Load Turnstile script once (idempotent across components) ──────
  useEffect(() => {
    if (!ENABLE_LOGIN_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (window.turnstile) return;
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  // ─── Render the Turnstile widget once the threshold is reached ──────
  useEffect(() => {
    if (!captchaRequired) return;
    if (!isOpen) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tryRender = () => {
      if (cancelled) return false;
      const container = turnstileContainerRef.current;
      if (!container || !window.turnstile) return false;
      if (turnstileWidgetIdRef.current) return true;
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (!cancelled) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          theme: 'light'
        });
        turnstileWidgetIdRef.current = widgetId;
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };
    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [captchaRequired, isOpen]);
  // When the modal closes, drop the widget reference so it re-renders cleanly
  // the next time the modal reopens past the threshold.
  useEffect(() => {
    if (isOpen) return;
    if (turnstileWidgetIdRef.current && window.turnstile?.remove) {
      try {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      } catch (err) {
        // Non-fatal — widget may have already been torn down.
      }
    }
    turnstileWidgetIdRef.current = null;
    setTurnstileToken(null);
  }, [isOpen]);
  const resetTurnstileWidget = () => {
    if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
    setTurnstileToken(null);
  };
  if (!isOpen) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !passport) {
      setError('Please enter both email and passport');
      return;
    }
    // ─── Turnstile gate (only active once threshold is reached) ──────
    if (captchaRequired) {
      if (!turnstileToken) {
        setError('Please complete the verification challenge before signing in.');
        return;
      }
      setIsLoading(true);
      const honeypotEl = document.getElementById('mc-tl-website-hp') as HTMLInputElement | null;
      const honeypot = honeypotEl?.value ?? '';
      const verifyFn = httpsCallable<{
        token: string;
        honeypot: string;
      }, {
        verified: boolean;
        errors?: string[];
      }>(functions, 'verifyTurnstile');
      let verifyResult;
      try {
        verifyResult = await verifyFn({
          token: turnstileToken,
          honeypot
        });
      } catch (verifyErr) {
        console.error('[Turnstile] verification call failed:', verifyErr);
        setError('Verification could not be completed. Please refresh the challenge and try again.');
        resetTurnstileWidget();
        setIsLoading(false);
        return;
      }
      if (!verifyResult.data?.verified) {
        console.warn('[Turnstile] verification rejected:', verifyResult.data?.errors);
        setError('Verification failed. Please complete the challenge again and resubmit.');
        resetTurnstileWidget();
        setIsLoading(false);
        return;
      }
    } else {
      setIsLoading(true);
    }
    try {
      console.log('=== TEACHER LOGIN ATTEMPT (MODAL) ===');
      console.log('Email:', email);
      console.log('Calling login with type: teacher');
      await login(email, passport, 'teacher');
      console.log('Login successful, navigating to dashboard');
      // Reset failure counter on success
      setFailedAttempts(0);
      // Close modal and navigate
      onClose();
      navigate('/teacher-dashboard');
    } catch (err: any) {
      console.error('=== TEACHER LOGIN ERROR (MODAL) ===');
      console.error('Login error:', err);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      // Determine if this is a credential-class failure (attacker signal)
      // vs a transient/environmental failure (not an attacker signal).
      // Only credential failures should increment the counter.
      const isNetwork = err.code === 'auth/network-request-failed';
      const isFirebaseRateLimit = err.code === 'auth/too-many-requests';
      if (isFirebaseRateLimit) {
        setError('Too many failed login attempts. Please try again later.');
      } else if (isNetwork) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Generic message for all auth failures (security best practice)
        setError('Invalid email or password');
        // Increment only on actual credential failures
        setFailedAttempts(n => n + 1);
      }
      // Always refresh the challenge after a failed attempt while the gate
      // is active, so a fresh token is required for the next retry.
      if (captchaRequired) {
        resetTurnstileWidget();
      }
    } finally {
      setIsLoading(false);
    }
  };
  const submitDisabled = isLoading || captchaRequired && !turnstileToken;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6" data-id="element-12076">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={onClose} data-id="element-12077"></div>

      {/* Modal */}
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl relative z-10 animate-fadeIn mx-4 my-auto overflow-hidden" data-id="element-12078">
        <div className="px-8 py-8" data-id="element-12079">
          <div className="flex justify-between items-start mb-6" data-id="element-12080">
            <div className="text-center w-full" data-id="element-12081">
              <h2 className="text-2xl font-serif font-bold text-navy-900 mb-1" data-id="element-12082">
                Teacher Login
              </h2>
              <p className="text-gray-600 text-sm" data-id="element-12083">
                Sign in to access your dashboard
              </p>
            </div>
            <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close" data-id="element-12084">
              <XIcon className="h-5 w-5" data-id="element-12085" />
            </button>
          </div>

          {error && <div className="mb-6 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-start text-sm" data-id="element-12086">
              <AlertCircleIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-12087" />
              <span data-id="element-12088">{error}</span>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-12089">
            <div data-id="element-12090">
              <label htmlFor="modal-email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12091">
                Email Address
              </label>
              <div className="relative" data-id="element-12092">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12093">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-12094" />
                </div>
                <input id="modal-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="you@example.com" required disabled={isLoading} data-id="element-12095" />
              </div>
            </div>

            <div data-id="element-12096">
              <div className="flex items-center justify-between mb-1" data-id="element-12097">
                <label htmlFor="modal-passport" className="block text-sm font-medium text-gray-700" data-id="element-12098">
                  Passport
                </label>
                <Link to="/forgot-password?role=teacher" className="text-xs text-blue-600 hover:text-blue-700" onClick={onClose} data-id="element-12099">
                  Forgot password?
                </Link>
              </div>
              <div className="relative" data-id="element-12100">
                <input type={showPassport ? 'text' : 'password'} id="modal-passport" value={passport} onChange={e => setPassport(e.target.value)} className="w-full px-4 py-2.5 pr-12 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="Enter your passport" required disabled={isLoading} data-id="element-12101" />
                <button type="button" onClick={() => setShowPassport(!showPassport)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none" aria-label={showPassport ? 'Hide passport' : 'Show passport'} data-id="element-12102">
                  {showPassport ? <EyeOff size={18} data-id="element-12103" /> : <Eye size={18} data-id="element-12104" />}
                </button>
              </div>
            </div>

            <div className="flex items-center" data-id="element-12105">
              <input id="modal-remember-me" name="remember-me" type="checkbox" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" data-id="element-12106" />
              <label htmlFor="modal-remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-12107">
                Remember me
              </label>
            </div>

            {/* ─── Honeypot (visually hidden, real users never fill) ─── */}
            {captchaRequired && <div aria-hidden="true" style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }} data-id="element-12108">
                <label htmlFor="mc-tl-website-hp" data-id="element-12109">Website (leave blank)</label>
                <input id="mc-tl-website-hp" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" data-id="element-12110" />
              </div>}

            {/* ─── Cloudflare Turnstile widget (only after threshold) ─── */}
            {captchaRequired && <div data-id="element-12111">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12112">
                  Human Verification
                </label>
                <div className="flex justify-center" data-id="element-12113">
                  <div ref={turnstileContainerRef} data-id="element-12114" />
                </div>
                <p className="mt-2 text-xs text-gray-500 text-center" data-id="element-12115">
                  Additional verification is required after multiple failed
                  sign-in attempts.
                </p>
              </div>}

            <button type="submit" disabled={submitDisabled} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-md hover:shadow-lg transform active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-12116">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-12117">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-12118"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-12119"></path>
                  </svg>
                  Signing In...
                </> : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center pt-4 border-t border-gray-100" data-id="element-12120">
            <p className="text-sm text-gray-600" data-id="element-12121">
              Don't have an account?{' '}
              <Link to="/teacher-signup" className="text-blue-600 hover:text-blue-700 font-medium" onClick={onClose} data-id="element-12122">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>;
};